/**
 * Legal Case Management Routes Configuration
 * Migrated from old ERP - Masters + Legal Case Processing + Reports
 */

import { MODULE_HEADER_BG } from '@/theme/moduleHeader';
import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { ModuleBannerProvider, type ModuleBanner } from '../../../../core/layout/moduleBanner';
import GlobalUdaHeaderSelect from '@/components/GlobalUdaHeaderSelect';

// Setup and Reports draw the slim module masthead in the LCMS slate -> indigo -> violet.
// Setup carries the working-UDA picker (LCMS has no UDA scope of its own); the report
// screens draw the picker the report data obeys themselves.
const LCMS_BANNER_BG = MODULE_HEADER_BG;
const LCMS_SETUP_BANNER: ModuleBanner = { background: LCMS_BANNER_BG, title: 'Setup', udaSlot: <GlobalUdaHeaderSelect /> };
const LCMS_REPORTS_BANNER: ModuleBanner = { background: LCMS_BANNER_BG, title: 'Reports' };
const LegalCaseManagementDashboard = lazy(() => import('../pages/LegalCaseManagementDashboard'));
const CaseList = lazy(() => import('../pages/CaseList'));
const ServiceRequests = lazy(() => import('../pages/ServiceRequests'));
const LegacyImport = lazy(() => import('../pages/LegacyImport'));
// `setup` and `reports` are served by the DB-driven core pages below, so the
// module's own landing pages are not routed (same as upstream Puramitra).
// const LCMSSetup = lazy(() => import('../pages/LCMSSetup'));
// const LCMSReports = lazy(() => import('../pages/LCMSReports'));

// Master components
const Advocate = lazy(() => import('../masters/advocate'));
const CaseType = lazy(() => import('../masters/case-type'));
const CourtType = lazy(() => import('../masters/court-type'));
const Court = lazy(() => import('../masters/court'));
const GovernmentDepartment = lazy(() => import('../masters/government-department'));
const InterimOrder = lazy(() => import('../masters/interim-order'));
const JudgmentType = lazy(() => import('../masters/judgment-type'));
const PetitionType = lazy(() => import('../masters/petition-type'));
const CaseStage = lazy(() => import('../masters/case-stage'));

// Report components
const DailyBoardReport = lazy(() => import('../pages/reports/DailyBoardReport'));
const TimeSeriesReport = lazy(() => import('../pages/reports/TimeSeriesReport'));
const GenericSubReport = lazy(() => import('../pages/reports/GenericSubReport'));
const DueDateReport = lazy(() => import('../pages/reports/DueDateReport'));
// const UlbCsvReports = lazy(() => import('../pages/reports/UlbCsvReports'));

// Import routes from core modules
import { reportsRoutes } from '@/modules/core/reports/routes';

// DB-driven setup landing page (shared core component)
const SetupPage = lazy(() => import('@/modules/core/setup'));

// Simple pass-through layout for nested routes
const PassThroughLayout = () => <Outlet />;

// LCM-specific setup children — DB stores option URLs as
// `/app/maud-erp/legal-case-management/setup/{name}`, so each must resolve here.
const lcmsSetupRoutes: RouteObject[] = [
  { index: true, element: <SetupPage /> },
  { path: 'advocate', element: <Advocate /> },
  { path: 'case-type', element: <CaseType /> },
  { path: 'court-type', element: <CourtType /> },
  { path: 'court', element: <Court /> },
  { path: 'government-department', element: <GovernmentDepartment /> },
  { path: 'interim-order', element: <InterimOrder /> },
  { path: 'judgment-type', element: <JudgmentType /> },
  { path: 'petition-type', element: <PetitionType /> },
  { path: 'case-stage', element: <CaseStage /> },
];

export const legalCaseManagementRoutes: RouteObject[] = [
  {
    index: true,
    element: <LegalCaseManagementDashboard />
  },
  {
    path: 'cases',
    element: <CaseList />
  },
  {
    path: 'service-requests',
    element: <ServiceRequests />
  },

  {
    // Bulk-loading an authority's historical register from a spreadsheet.
    path: 'import',
    element: <LegacyImport />
  },


  {
    path: 'reports',
    element: <ModuleBannerProvider value={LCMS_REPORTS_BANNER}><PassThroughLayout /></ModuleBannerProvider>,
    children: reportsRoutes
  },

  {
    path: 'setup',
    element: <ModuleBannerProvider value={LCMS_SETUP_BANNER}><PassThroughLayout /></ModuleBannerProvider>,
    children: lcmsSetupRoutes
  },
  // Individual report pages
  {
    path: 'reports/daily-board',
    element: <DailyBoardReport />
  },
  {
    path: 'reports/time-series',
    element: <TimeSeriesReport />
  },
  {
    path: 'reports/generic-sub',
    element: <GenericSubReport />
  },
  {
    path: 'reports/due-date',
    element: <DueDateReport />
  },
  // {
  //   path: 'reports/ulb-csv',
  //   element: <UlbCsvReports />
  // },
  // Master routes
  {
    path: 'masters/advocate',
    element: <Advocate />
  },
  {
    path: 'masters/case-type',
    element: <CaseType />
  },
  {
    path: 'masters/court-type',
    element: <CourtType />
  },
  {
    path: 'masters/court',
    element: <Court />
  },
  {
    path: 'masters/government-department',
    element: <GovernmentDepartment />
  },
  {
    path: 'masters/interim-order',
    element: <InterimOrder />
  },
  {
    path: 'masters/judgment-type',
    element: <JudgmentType />
  },
  {
    path: 'masters/petition-type',
    element: <PetitionType />
  },
  {
    path: 'masters/case-stage',
    element: <CaseStage />
  }
];

export default legalCaseManagementRoutes;
